import { useState } from 'react';
import { CircleUser } from 'lucide-react';
import { apiPersons } from '../api/client.js';
import PersonFormModal from './PersonFormModal.jsx';
import Modal from './Modal.jsx';

/**
 * « Mon profil » — point d'entrée UI accessible à TOUT utilisateur connecté
 * (y compris le rôle USER simple qui n'a aucun autre accès). Au clic, récupère
 * la fiche Person liée au compte courant via `GET /persons/me` puis ouvre
 * `PersonFormModal` en mode restreint (`editableFields=['avatarUrl']`) : seule
 * la photo de profil est éditable.
 *
 * Cas 404 (aucune Person liée au compte) → message clair, pas de crash.
 *
 * Après upload+save, on remonte la Person via `onSaved` pour rafraîchir
 * l'avatar éventuellement affiché dans le header/nav.
 */
export default function MyProfileButton({ onSaved, className, iconSize = 16 }) {
  const [loading, setLoading] = useState(false);
  const [personId, setPersonId] = useState(null);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);

  const openProfile = async () => {
    if (loading) return;
    setError(null);
    setLoading(true);
    try {
      const me = await apiPersons.me();
      setPersonId(me.id);
      setOpen(true);
    } catch (err) {
      // 404 = aucune fiche collaborateur liée au compte.
      if (err?.status === 404) {
        setError('Aucune fiche collaborateur liée à votre compte.');
      } else {
        setError(err?.message || 'Impossible de charger votre profil.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={openProfile}
        disabled={loading}
        title="Mon profil"
        aria-label="Mon profil"
        className={className}
        style={
          className
            ? { cursor: loading ? 'wait' : 'pointer', opacity: loading ? 0.6 : 1 }
            : {
                background: 'transparent',
                color: 'var(--text)',
                border: '1px solid var(--glass-border)',
                borderRadius: 'var(--radius-sm)',
                padding: 6,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: loading ? 'wait' : 'pointer',
                opacity: loading ? 0.6 : 1,
              }
        }
      >
        <CircleUser size={iconSize} />
      </button>

      {open && personId ? (
        <PersonFormModal
          open={open}
          personId={personId}
          editableFields={['avatarUrl']}
          onClose={() => setOpen(false)}
          onSaved={(person) => {
            onSaved?.(person);
            setOpen(false);
          }}
        />
      ) : null}

      {/* Cas d'erreur (404 ou autre) : petite modale d'information centrée. */}
      <Modal
        open={Boolean(error)}
        onClose={() => setError(null)}
        size="sm"
        title="Mon profil"
        footer={
          <button
            type="button"
            onClick={() => setError(null)}
            style={{
              background: 'var(--accent)',
              color: '#fff',
              border: 'none',
              borderRadius: 'var(--radius-pill)',
              padding: '8px 16px',
              fontSize: 13,
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Fermer
          </button>
        }
      >
        <div style={{ padding: 4, color: 'var(--text)', fontSize: 14 }}>{error}</div>
      </Modal>
    </>
  );
}
